import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';

function ConfirmDialog({ enquiry, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [sponsorName, setSponsorName] = useState(enquiry.sponsor_name);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/confirm`, { sponsor_name_override: sponsorName, sponsorship_start_date: startDate });
      toast.success(`${enquiry.animal_name} is now Sponsored.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not confirm.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] p-6 border border-border" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs uppercase  text-muted-foreground">Confirm sponsorship</p>
        <h3 className="font-extrabold text-xl mt-1">{enquiry.animal_name}</h3>
        <div className="space-y-3 mt-4">
          <div>
            <label className="text-sm text-muted-foreground">Sponsor name (as displayed)</label>
            <input value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-transparent px-3 h-10 text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-transparent px-3 h-10 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-xl bg-[#1E3A2F]">
            {loading ? 'Confirming…' : 'Confirm sponsorship'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/enquiries', { params: { status_filter: status } });
      setItems(data);
    } catch (e) { console.error('Failed to load enquiries:', e); toast.error('Failed to load'); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decline = async (e) => {
    if (!window.confirm(`Decline enquiry for ${e.animal_name}?`)) return;
    try {
      await api.post(`/admin/enquiries/${e.id}/decline`);
      toast.success('Enquiry declined');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const statusBadge = (s) => (
    <Badge variant="outline" className={s === 'pending' ? 'border-[#E4DDC5] text-[#5C4C1F]' : s === 'confirmed' ? 'border-[#1E3A2F]/40 text-[#1E3A2F]' : 'text-muted-foreground'}>{s}</Badge>
  );

  return (
    <div data-testid={ADMIN.enquiriesPage} className="space-y-6 max-w-5xl">
      <div>
        <p className="text-xs uppercase  text-muted-foreground">Requests</p>
        <h1 className="font-extrabold text-3xl mt-1">Enquiries</h1>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="bg-[hsl(var(--secondary))]">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="declined">Declined</TabsTrigger>
        </TabsList>
        <TabsContent value={status} className="mt-4 space-y-3">
          {items.length === 0 ? (
            <Card className="rounded-2xl p-8 text-center text-muted-foreground border-border">No {status} enquiries.</Card>
          ) : items.map((e) => (
            <Card data-testid={ADMIN.enquiryRow} key={e.id} className="rounded-2xl p-5 border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-extrabold text-lg">{e.animal_name}</p>
                    {statusBadge(e.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{e.sponsor_name} · Member #{e.membership_number} · {e.email} · {e.phone}</p>
                  {e.message && <p className="text-sm mt-2 italic">“{e.message}”</p>}
                  <p className="text-xs text-muted-foreground mt-2">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                {e.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button data-testid={ADMIN.enquiryConfirmButton} onClick={() => setConfirmTarget(e)} size="sm" className="rounded-lg bg-[#1E3A2F]">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm
                    </Button>
                    <Button data-testid={ADMIN.enquiryDeclineButton} onClick={() => decline(e)} size="sm" variant="outline" className="rounded-lg">
                      <XCircle className="h-4 w-4 mr-1.5" /> Decline
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {confirmTarget && <ConfirmDialog enquiry={confirmTarget} onCancel={() => setConfirmTarget(null)} onDone={() => { setConfirmTarget(null); load(); }} />}
    </div>
  );
}
